import { useState } from "react";
import {
  Modal, Button, Input, Select, DatePicker, Row, Col, Badge,
} from "antd";
import { CloseOutlined } from "@ant-design/icons";
import filtericon from "assets/account-filter.svg";
import styled from "@emotion/styled";
import dayjs from "dayjs";

const { Option } = Select;
const { RangePicker } = DatePicker;

const FilterModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;
    padding: 0;
    overflow: hidden;
  }
  .ant-modal-header {
    padding: 18px 24px 14px;
    border-bottom: 1px solid #f0f0f0;
    margin: 0;
  }
  .ant-modal-title {
    font-size: 15px;
    font-weight: 600;
    color: #111;
  }
  .ant-modal-close {
    top: 14px;
    right: 16px;
  }
  .ant-modal-body {
    padding: 20px 24px 10px;
  }
  .ant-modal-footer {
    padding: 12px 24px 18px;
    border-top: 1px solid #f0f0f0;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    margin: 0;
  }
`;

const FieldLabel = styled.label`
  font-size: 12px;
  font-weight: 500;
  color: #555;
  display: block;
  margin-bottom: 6px;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  .ant-select-selector {
    border-radius: 20px !important;
    min-height: 36px !important;
    height: auto !important;
    align-items: center;
    display: flex !important;
    font-size: 13px;
    flex-wrap: wrap;
    padding: 2px 8px !important;
  }
  &.ant-select-focused .ant-select-selector,
  &:hover .ant-select-selector {
    border-color: #E2E2E8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 20px !important;
  height: 36px;
  font-size: 13px;
  &:hover,
  &:focus {
    border-color: #E2E2E8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const StyledRangePicker = styled(RangePicker)`
  width: 100%;
  border-radius: 20px !important;
  height: 36px;
  font-size: 13px;

  .ant-picker-input > input {
    font-size: 13px;
  }

  .ant-picker-range-separator {
    padding: 0 8px;
    color: #999;
  }

  &:hover,
  &.ant-picker-focused {
    border-color: #E2E2E8 !important;
    box-shadow: 0 0 0 2px rgba(82, 198, 176, 0.15) !important;
  }
`;

const ClearBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  border-color: #000 !important;
  border: 1.5px solid #000;
  color: #000 !important;
  background: white;
  &:hover,
  &:focus {
    border-color: #000 !important;
    color: #000 !important;
  }
`;

const ApplyBtn = styled(Button)`
  border-radius: 6px;
  height: 36px;
  font-size: 13px;
  background-color: #00685F;
  border-color: #00685F;
  color: #fff !important;
  &:hover,
  &:focus {
     background-color: #00685F;
  border-color: #00685F;
  color: #fff !important;
  }
`;

const FilterIconBtn = styled(Button)`
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid #deefee;
  background: #deefee;
  color: #000;
  padding: 0;
  &:hover,
  &:focus {
    border-color: #deefee !important;
    color: #000!important;
    background: #deefee !important;
  }
`;

const FieldGroup = styled.div`
  margin-bottom: 16px;
`;

const countActiveFilters = (filters) => {
  const { dateFrom, dateTo, ...rest } = filters;
  const restCount = Object.values(rest).filter((v) => {
    if (v === null || v === undefined || v === "") return false;
    if (Array.isArray(v) && v.length === 0) return false;
    return true;
  }).length;
  const dateCount = (dateFrom || dateTo) ? 1 : 0;
  return restCount + dateCount;
};

const TransactionFilterModal = ({
  sourceOptions = [],
  destinationOptions = [],
  onApply,
  onClear,
  loading = false,
}) => {
  const emptyFilters = {
    source: [],
    destination: [],
    fundIn: "",
    fundOut: "",
    fundBalance: "",
    dateFrom: null,
    dateTo: null,
  };

  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState(emptyFilters);
  const [appliedFilters, setAppliedFilters] = useState(emptyFilters);

  const set = (key) => (val) => setFilters((prev) => ({ ...prev, [key]: val }));

  const handleDateRange = (dates) => {
    const [from, to] = dates ?? [];
    setFilters((prev) => ({
      ...prev,
      dateFrom: from && from.isValid() ? from.startOf("day").toISOString() : null,
      dateTo: to && to.isValid() ? to.endOf("day").toISOString() : null,
    }));
  };

  const handleClear = () => {
    setFilters(emptyFilters);
    setAppliedFilters(emptyFilters);
    onClear?.();
  };

  const handleApply = () => {
    setAppliedFilters(filters);
    onApply?.(filters);
    setOpen(false);
  };

  const handleCancel = () => {
    setFilters(appliedFilters);
    setOpen(false);
  };

  const badgeCount = countActiveFilters(appliedFilters);
  const liveCount = countActiveFilters(filters);

  return (
    <>
      <Badge count={badgeCount} size="small" color="red" offset={[-2, 2]}>
        <FilterIconBtn
          active={badgeCount > 0 ? 1 : 0}
          icon={<img src={filtericon} alt="Filter" />}
          onClick={() => setOpen(true)}
        />
      </Badge>

      <FilterModal
        open={open}
        onCancel={handleCancel}
        title={`Filter${liveCount > 0 ? ` (${liveCount})` : ""}`}
        width={900}
        centered
        destroyOnClose={false}
        closeIcon={<CloseOutlined style={{ fontSize: 13 }} />}
        footer={[
          <ClearBtn key="clear" onClick={handleClear}>
            Clear All
          </ClearBtn>,
          <ApplyBtn key="apply" onClick={handleApply} loading={loading}>
            Apply Filter
          </ApplyBtn>,
        ]}
      >
        <Row gutter={[16, 0]}>
          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Source</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.source}
                onChange={set("source")}
                allowClear
                maxTagCount="responsive"
              >
                {sourceOptions.map((s) => (
                  <Option key={s} value={s}>{s}</Option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </Col>

          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Destination</FieldLabel>
              <StyledSelect
                mode="multiple"
                placeholder="All"
                value={filters.destination}
                onChange={set("destination")}
                allowClear
                maxTagCount="responsive"
              >
                {destinationOptions.map((d) => (
                  <Option key={d} value={d}>{d}</Option>
                ))}
              </StyledSelect>
            </FieldGroup>
          </Col>

          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Fund in</FieldLabel>
              <StyledInput
                placeholder="e.g. 200"
                type="number"
                min={0}
                value={filters.fundIn}
                onChange={(e) => set("fundIn")(e.target.value)}
              />
            </FieldGroup>
          </Col>

          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Fund Out</FieldLabel>
              <StyledInput
                placeholder="e.g. 100"
                type="number"
                min={0}
                value={filters.fundOut}
                onChange={(e) => set("fundOut")(e.target.value)}
              />
            </FieldGroup>
          </Col>

          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Fund Balance</FieldLabel>
              <StyledInput
                placeholder="e.g. 400"
                type="number"
                min={0}
                value={filters.fundBalance}
                onChange={(e) => set("fundBalance")(e.target.value)}
              />
            </FieldGroup>
          </Col>

          <Col span={6}>
            <FieldGroup>
              <FieldLabel>Date of Transaction</FieldLabel>
              <StyledRangePicker
                placeholder={["From", "To"]}
                format="DD-MM-YYYY"
                value={
                  filters.dateFrom && filters.dateTo
                    ? [dayjs(filters.dateFrom), dayjs(filters.dateTo)]
                    : undefined
                }
                onChange={handleDateRange}
                separator={<span style={{ color: "#999", fontSize: 14 }}>↔</span>}
              />
            </FieldGroup>
          </Col>
        </Row>
      </FilterModal>
    </>
  );
};

export default TransactionFilterModal;